import React from "react";
import { DataGrid, GridColDef, GridColTypeDef } from "@mui/x-data-grid";
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";

import WidgetAlert, { WidgetErrorType } from "components/widgetParts/WidgetAlert";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import { Query } from "system/query/Query";
import { DashboardQueryDrilldownDialog } from "components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog";
import { ColumnModelItem, ColumnRenderType } from "components/tables/columns/ColumnModel";
import { topicTableDefaultSettings } from "./topicTableUtils";
import textColumn from "components/tables/columns/TextColumn";
import sentimentBarColumn from "components/tables/columns/SentimentBarColumn";
import numberColumn from "components/tables/columns/NumberColumn";
import percentColumn from "components/tables/columns/PercentColumn";
import distributionBarColumn from "components/tables/columns/DistributionBarColumn";
import chipColumn from "components/tables/columns/ChipColumn";
import dateTimeColumn from "components/tables/columns/DateTimeColumn";
import colorBoxColumn from "components/tables/columns/ColorBoxColumn";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from "system/widget/Widget";
import StripedDataGrid from "components/uiElements/dataGrid/StripedDataGrid";

interface TopicRelationship {
  cooccurrence: number;
  index: number;
  label: string;
  similarity: number;
}

interface Topic {
  appearances: number;
  cluster_id: number;
  count: number;
  distribution: number;
  est_total_appearances: number;
  est_total_count: number;
  est_total_negative_count: number;
  est_total_positive_count: number;
  label: string;
  language: string;
  negative_count: number;
  positive_count: number;
  positivity: number;
  relationships: TopicRelationship;
  relevance: number;
  sentimental: number;
  velocity: number;
  query: Query
}

const _getColumnType: Record<string, GridColTypeDef> = {
  "chip": chipColumn(),
  "colorbox": colorBoxColumn(),
  "distributionBar": distributionBarColumn(),
  "latitude_longitude": chipColumn(),
  "number": numberColumn(),
  "percent": percentColumn(),
  "sentimentBar": sentimentBarColumn(),
  "text": textColumn(),
  "timestamp": dateTimeColumn()
};

export const initialTopicColumns: ColumnModelItem[] = [
  { id: "label", field: "label", headerName: "label", fieldType: "text", sourceField: "label", renderType: ColumnRenderType.Text },
  { id: "relevance", field: "relevance", headerName: "relevance", fieldType: "number", sourceField: "relevance", renderType: ColumnRenderType.Number },
  { id: "count", field: "count", headerName: "count", fieldType: "number", sourceField: "count", renderType: ColumnRenderType.Number },
  { id: "est_total_count", field: "est_total_count", headerName: "est_total_count", fieldType: "number", sourceField: "est_total_count", renderType: ColumnRenderType.Number },
  { id: "appearances", field: "appearances", headerName: "appearances", fieldType: "number", sourceField: "appearances", renderType: ColumnRenderType.Number },
  { id: "distribution", field: "distribution", headerName: "distribution", fieldType: "percent", sourceField: "distribution", renderType: ColumnRenderType.DistributionBar },
  { id: "velocity", field: "velocity", headerName: "velocity", fieldType: "percent", sourceField: "velocity", renderType: ColumnRenderType.Percent },
  { id: "est_total_appearances", field: "est_total_appearances", headerName: "est_total_appearances", fieldType: "number", sourceField: "est_total_appearances", renderType: ColumnRenderType.Number },
  { id: "positive_count", field: "positive_count", headerName: "positive_count", fieldType: "number", sourceField: "positive_count", renderType: ColumnRenderType.Number },
  { id: "negative_count", field: "negative_count", headerName: "negative_count", fieldType: "number", sourceField: "negative_count", renderType: ColumnRenderType.Number },
  { id: "est_total_positive_count", field: "est_total_positive_count", headerName: "est_total_positive_count", fieldType: "number", sourceField: "est_total_positive_count", renderType: ColumnRenderType.Number },
  { id: "est_total_negative_count", field: "est_total_negative_count", headerName: "est_total_negative_count", fieldType: "number", sourceField: "est_total_negative_count", renderType: ColumnRenderType.Number },
  { id: "positivity", field: "positivity", headerName: "positivity", fieldType: "percent", sourceField: "positivity", renderType: ColumnRenderType.SentimentBar },
  { id: "sentimental", field: "sentimental", headerName: "sentimental", fieldType: "percent", sourceField: "sentimental", renderType: ColumnRenderType.Number },
  { id: "language", field: "language", headerName: "language", fieldType: "text", sourceField: "language", renderType: ColumnRenderType.Chip },
  { id: "cluster_id", field: "cluster_id", headerName: "cluster", fieldType: "number", sourceField: "cluster_id", renderType: ColumnRenderType.Colorbox },
];

// TODO: Should we handle API pagination?  Could this be made reusable?
function TopicTableWidget(props: WithQueryWidgetProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'topic-table');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'topic-table');

  const data: Array<Topic> = apiResponseInfo?.responseBody?.topics ?? [];
  const fieldLabels: Record<string, string> = apiResponseInfo?.responseBody?.field_labels ?? [];

  const [selectedRow, setSelectedRow] = React.useState<Topic | null>(null);
  const handleRowClick = (row: Topic) => {
    setSelectedRow(row);
  };

  const useEstTotalCount = data?.some((narrative) => narrative.count !== narrative.est_total_count);

  const currentState = {
    ...topicTableDefaultSettings,
    ...props.widget?.data?.settings
  };

  const initialModelColumns = initialTopicColumns.map(col => {
    return {
      ...col,
      key: col.sourceField,
      field: col.field,
      headerName: fieldLabels[col.field],
      fieldType: col.fieldType,
      sourceField: col.sourceField,
      renderType: col.renderType,
    };
  });

  const widgetModelColumns: ColumnModelItem[] = props?.widget.data?.columnModel?.columns ?? initialModelColumns;

  const dataGridColumns: GridColDef[] = widgetModelColumns.reduce((previous: Array<GridColDef>, current, index) => {
    if (useEstTotalCount && current.id === "count"
      || !useEstTotalCount && current.id === "est_total_count"
    ) {
      return previous;
    }

    let headerName = current.headerName === fieldLabels[current.field] ? fieldLabels[current.field] : current.headerName;
    
    if (current.field === "cluster_id") {
      headerName = i18n.t("cluster");
    }

    previous.push({
      ...current,
      field: current.field,
      headerName,
      hideable: true,
      headerAlign: 'center',
      sortable: false,
      ...(current.renderType ? _getColumnType[current.renderType] : textColumn()),
      ...(current.renderType === "timestamp" &&
        {
          valueGetter: (value: any) => new Date(value)
        }
      ),
    });
    return previous;
  }, []);

  // Converts ColumnModel format to MUI's DataGrid format
  const modelToMUIInitialState = (): GridInitialStateCommunity => {
    // If no model columns, use default columns
    const orderedFields = widgetModelColumns.map((item: ColumnModelItem) => item.field);
    const columnVisibilityModel = widgetModelColumns.reduce((acc, curr) => {
      if (curr.hidden === true) {
        return {
          ...acc,
          [curr.field]: false
        };
      } else {
        return acc;
      }
    }, {}) ?? {};

    return {
      columns: {
        orderedFields,
        columnVisibilityModel
      }
    };
  };

  const someColumnsVisible = () => widgetModelColumns.some((col: ColumnModelItem) => (col.hidden === false || !("hidden" in col)));

  return (
    <>
      {data.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              defaultTitle={i18n.t("widget-types.topic-table.label")}
              settingsTitle={currentState.widgetTitle}
              style={{ pb: 2 }}
              widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
          }
        >
          {someColumnsVisible() ?
            <StripedDataGrid
              showRowStripes={currentState.rowStripes}
              columns={dataGridColumns}
              density={currentState?.density ?? "standard"}
              disableColumnSelector
              disableColumnMenu
              getRowId={(row: Topic) => row.label}
              initialState={modelToMUIInitialState()}
              rows={data}
              onRowClick={(rowData) => {
                handleRowClick(rowData.row);
              }}
            /> :
            <WidgetAlert severity="warning">{i18n.t("tables.all-columns-removed")}</WidgetAlert>
          }
        </WidgetLayout>
      }
      {data.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
      {selectedRow &&
         <DashboardQueryDrilldownDialog
           title={selectedRow.label ?? ""}
           queryInfo={props.queryInfo}
           onClose={() => setSelectedRow(null)}
           activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
           drilldownQuery={new Query({ query: selectedRow?.query })}
           widgetRequestInfo={props.widget?.request_info}
         />
       }
    </>
  )
}

export default withQueryWidget(TopicTableWidget, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
